/**
 * contractToll — 12/02/2026
 *
 * Helper pur qui calcule le coût de péage quotidien estimé pour un
 * contrat donné. Reproduit exactement la formule utilisée dans
 * `store/helpers/computeDailyExpenses.js` afin de ne jamais désynchroniser
 * l'affichage des contrats et le débit réel de trésorerie.
 *
 * Règle (constante entre les 2 endroits) :
 *   - Distance < 30 km  → péage 0 € (missions urbaines/régionales)
 *   - Sinon : min(50 €, km × 0,08 €/km) par jour et par véhicule
 *   - Tier ≥ 5 (convois exceptionnels) : × 1,5
 */

const TOLL_PER_KM = 0.08;
const TOLL_DAILY_CAP = 50;
const TOLL_MIN_DISTANCE_KM = 30;
const TOLL_HEAVY_TIER_MULTIPLIER = 1.5;
const TOLL_HEAVY_TIER_THRESHOLD = 5;

/**
 * Coût de péage journalier estimé pour un contrat.
 * @param {Object} contract - contract snapshot (distanceKm requis)
 * @param {Object} [vehicle] - véhicule assigné (facultatif, sert au surcoût tier)
 * @returns {number} coût quotidien de péage en euros
 */
export function estimateContractDailyToll(contract, vehicle = null) {
  if (!contract) return 0;
  const km = contract.distanceKm || 0;
  if (km < TOLL_MIN_DISTANCE_KM) return 0;
  let toll = Math.min(TOLL_DAILY_CAP, km * TOLL_PER_KM);
  if (vehicle && (vehicle.tier || 1) >= TOLL_HEAVY_TIER_THRESHOLD) {
    toll *= TOLL_HEAVY_TIER_MULTIPLIER;
  }
  return Math.round(toll * 100) / 100;
}

/**
 * Coût de péage total estimé pour la durée du contrat (utile pour la
 * marge prévisionnelle et l'affichage détaillé).
 */
export function estimateContractTotalToll(contract, vehicle = null) {
  if (!contract) return 0;
  const daily = estimateContractDailyToll(contract, vehicle);
  if (contract.frequency === 'ponctuel') {
    // Missions ponctuelles : 1 jour de péage aller + retour (approx).
    return Math.round(daily * 2);
  }
  const days = contract.duration || 1;
  return Math.round(daily * days);
}
